import { Inject, Injectable, Optional } from '@angular/core';
import { combineLatest, firstValueFrom, Observable, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';
import { CRMFieldSchemaApiService } from '@vendasta/crm';
import { DYNAMIC_COMPONENT_DATA_TOKEN } from '../dependencies/tokens';
import { VariableMenuItem } from '../components/dynamic-component-selector';
import { SENDER_ID_TOKEN } from '../../../email-activity/src/dependencies';

export interface CrmDynamicContent {
  displayName: string;
  templateReplacementTag: string;
}

@Injectable({
  providedIn: 'root',
})
export class CrmDynamicFieldService {
  constructor(
    @Inject(SENDER_ID_TOKEN) private readonly senderId$: Observable<string>,
    @Inject(DYNAMIC_COMPONENT_DATA_TOKEN) @Optional() readonly dynamicComponents: VariableMenuItem[],
    private readonly crmFieldSchemaApiService: CRMFieldSchemaApiService,
  ) {}

  private readonly objectTypeTags: Record<string, string> = {
    Company:
      this.dynamicComponents?.find(
        (v) => v.textKey === 'EMAIL_BUILDER.DYNAMIC_COMPONENT_SELECTOR.PRIMARY_COMPANY_DETAILS',
      )?.replacementTags?.[0] || '',
    Contact:
      this.dynamicComponents?.find((v) => v.textKey === 'EMAIL_BUILDER.DYNAMIC_COMPONENT_SELECTOR.CONTACT_DETAILS')
        ?.replacementTags?.[0] || '',
  };

  readonly contactFields$ = this.senderId$.pipe(
    switchMap((namespace) => {
      return this.crmFieldSchemaApiService.listFieldSchema({
        namespace: namespace,
        crmObjectType: 'Contact',
        pagingOptions: {
          pageSize: 200,
        },
      });
    }),
    map((resp) => resp.fieldSchemas || []),
    map((fieldSchemas) => {
      return fieldSchemas.reduce(
        (fieldsMap, schema) => {
          fieldsMap[schema.fieldId] = {
            displayName: schema.fieldName,
            templateReplacementTag: `${this.objectTypeTags['Contact']}['__Contact_ID__', '${schema.fieldId}']`,
          } as CrmDynamicContent;
          return fieldsMap;
        },
        {} as Record<string, CrmDynamicContent>,
      );
    }),
    catchError((_) => {
      console.error('Failed to fetch contact fields for email-builder');
      return of({} as Record<string, CrmDynamicContent>);
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly associatedCompanyFields$ = this.senderId$.pipe(
    switchMap((namespace) => {
      return this.crmFieldSchemaApiService.listFieldSchema({
        namespace: namespace,
        crmObjectType: 'Company',
        pagingOptions: {
          pageSize: 200,
        },
      });
    }),
    map((resp) => resp.fieldSchemas || []),
    map((fieldSchemas) => {
      return fieldSchemas.reduce(
        (fieldsMap, schema) => {
          fieldsMap[schema.fieldId] = {
            displayName: schema.fieldName,
            templateReplacementTag: `${this.objectTypeTags['Company']}['__Contact_ID__', '${schema.fieldId}']`, //__Contact_ID__ is expected and required for hydration atm
          } as CrmDynamicContent;
          return fieldsMap;
        },
        {} as Record<string, CrmDynamicContent>,
      );
    }),
    catchError((_) => {
      console.error('Failed to fetch company fields for email-builder');
      return of({} as Record<string, CrmDynamicContent>);
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  private readonly allFields$ = combineLatest([this.associatedCompanyFields$, this.contactFields$]).pipe(
    map(([companyFields, contactFields]) => {
      return { ...companyFields, ...contactFields } as Record<string, CrmDynamicContent>;
    }),
  );

  async convertTemplateTagToHumanReadable(html: string): Promise<string> {
    if (!html) return html;
    const fieldMappings = await firstValueFrom(this.allFields$);
    return this.convertTemplateTagToHumanReadableHelper(html, fieldMappings);
  }

  convertTemplateTagToHumanReadable$(html$: Observable<string>): Observable<string> {
    return combineLatest([html$, this.allFields$]).pipe(
      map(([html, fieldMappings]) => {
        return this.convertTemplateTagToHumanReadableHelper(html, fieldMappings);
      }),
    );
  }

  private getObjectTypePrefixTag(html: string): string {
    for (const objectTypeTagsKey in this.objectTypeTags) {
      const tagValue = this.objectTypeTags[objectTypeTagsKey];
      if (tagValue && html.includes(tagValue)) {
        return objectTypeTagsKey.toLowerCase() + '.';
        break;
      }
    }
    return '';
  }

  private convertTemplateTagToHumanReadableHelper(
    html: string,
    fieldMappings: Record<string, CrmDynamicContent>,
  ): string {
    const templateTagRegex = /\{\{\s*([^}]+)\s*\}\}/g; //all dynamic content replacements
    const fieldIDRegex = /FieldID-([^'"\s\]]+)/; //matches field-ids
    return html.replace(templateTagRegex, (match, content) => {
      const fieldIdMatch = content.match(fieldIDRegex);
      if ((fieldIdMatch || []).length === 0) {
        return `<span contenteditable="false" data-original="${match}">${match}</span>`;
      }

      let displayValue = fieldIdMatch[0] || 'unknown';
      const fieldMatchedName = fieldMappings[fieldIdMatch?.[0]]?.displayName;
      if (fieldMatchedName && fieldMatchedName.length > 0) {
        displayValue = fieldMatchedName
          .trim()
          // eslint-disable-next-line no-useless-escape
          .split(/[\s\/\-\\|&+:;,.<>?!@#$%^*()[\]{}="'`~]+/) // Split by spaces and special characters
          .filter((word) => word.length > 0)
          .map((word, index) => {
            const cleanWord = word.replace(/[^a-zA-Z0-9]/g, ''); // Remove special chars
            if (cleanWord.length === 0) return '';
            return index === 0
              ? cleanWord.toLowerCase() // Convert to camel case
              : cleanWord.charAt(0).toUpperCase() + cleanWord.slice(1).toLowerCase();
          })
          .filter((word) => word.length > 0) // Remove any empty strings after cleaning
          .join('');
      }

      const objectTypePrefix = this.getObjectTypePrefixTag(content);

      return `<span contenteditable="false" data-original="${match}">{{ ${objectTypePrefix}${displayValue} }}</span>`;
    });
  }

  convertHumanReadableToTemplateTag(html: string): string {
    if (!html) return html;
    return html.replace(/<span [^>]*data-original="([^"]*)"[^>]*>[^<]*<\/span>/g, '$1');
  }

  convertRichTextToPlainTextWithDynamicVariables(html: string): string {
    if (!html) return html;
    let result = this.convertHumanReadableToTemplateTag(html);

    // Replace self-closing tags that should add space with actual spaces
    result = result.replace(/<br\s*\/?>/gi, ' ');
    result = result.replace(/<hr\s*\/?>/gi, ' ');

    // Remove all other HTML tags
    result = result.replace(/<[^>]*>/g, '');

    // Decode common HTML entities
    result = result
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>');

    // Update all HTML tags to have spaces to disable effect
    result = result.replace(/<(\/?[^>\s]+)(\s[^>]*)?>/g, '< $1$2 >');

    // Clean up extra whitespace
    result = result.replace(/\s+/g, ' ').trim();
    return result;
  }
}
